import { CommonUtility } from '@utility/common';
import { APIPath } from '../constants/api';
import { BaseService } from './base';
import { CRUDService } from './crud';

export type WalletAutoReloadSettings = {
  enabled: boolean;
  threshold_cents: number;
  reload_amount_cents: number;
  payment_method_id: string | null;
  version: number;
  can_edit: boolean;
};
export type WalletAutoReloadUpdate = Omit<WalletAutoReloadSettings, 'can_edit'>;

export type WalletPaymentRetry = {
  targetId: string;
  paymentMethodId: string;
  kind: 'card' | 'bank';
  policy: 'card' | 'once' | 'new_method' | 'fresh_authorization';
  canRetry: boolean;
  requestId?: string;
  requestState?: 'ready' | 'processing' | 'cancelled';
};
class Wallet extends CRUDService {
  constructor() {
    super(APIPath.wallet);
  }

  addBalance = data => BaseService.post(APIPath.walletBalance, data);

  addAdminBalance = data => BaseService.post(APIPath.walletAdminBalance, data);

  getBalance = () => BaseService.get(APIPath.walletBalance);

  getAutoReload = (): Promise<{ data: WalletAutoReloadSettings }> =>
    BaseService.get(`${APIPath.wallet}/auto-reload`);

  putAutoReload = (
    data: WalletAutoReloadUpdate,
  ): Promise<{ data: WalletAutoReloadSettings }> =>
    BaseService.put(`${APIPath.wallet}/auto-reload`, data);

  getPaymentRetries = (): Promise<{
    data: WalletPaymentRetry[];
    can_edit: boolean;
  }> => BaseService.get(`${APIPath.wallet}/payment-retries`);

  retryPayment = (data: { request_id: string; target_id: string }) =>
    BaseService.post(`${APIPath.wallet}/retry`, data);

  transactions = reqParams => {
    const params = CommonUtility.objectToParams(reqParams);
    return BaseService.get(`${APIPath.walletHistory}?${params}`);
  };

  linkToken = () => BaseService.get(APIPath.walletLinkToken);

  getThirdPartyServices = () =>
    BaseService.get(APIPath.walletThirdPartyServices);

  createThirdPartyServices = data =>
    BaseService.post(APIPath.walletThirdPartyServices, data);

  deleteThirdPartyServices = id =>
    BaseService.remove(`${APIPath.walletThirdPartyServices}/${id}`);

  makeDefault = data => BaseService.post(APIPath.wallet, data);

  refundTransaction = data => BaseService.post(APIPath.walletAdminRefund, data);

  getTransactionPDF = transactionId =>
    BaseService.get(`${APIPath.walletReciept}/${transactionId}`);

  getTransactionPDFs = reqParams => {
    const params = CommonUtility.objectToParams(reqParams);
    return BaseService.get(`${APIPath.walletReciept}/?${params}`);
  };

  exportTransactions = async (range: {
    start_date: string;
    end_date: string;
  }): Promise<void> => {
    const params = CommonUtility.objectToParams({
      ...range,
      type: 'transactions',
    });
    const csv = await BaseService.get<string>(
      `${APIPath.walletReciept}/?${params}`,
      false,
      true,
      undefined,
      { responseType: 'text' },
    );
    if (typeof csv !== 'string') throw new Error('Invalid wallet CSV response');
    const url = URL.createObjectURL(
      new Blob([csv], { type: 'text/csv;charset=utf-8' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = 'wallet_transactions.csv';
    document.body.appendChild(link);
    try {
      link.click();
    } finally {
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  };

  confirmBalance = data =>
    BaseService.put(`${APIPath.walletBalance}/confirm`, data);

  getWireDetails = () => BaseService.get(APIPath.walletWireDetails);
}

const WalletService = new Wallet();
Object.freeze(WalletService);
export default WalletService;
